import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { CONTENT, useContent } from "./content";
import { useHomeLang } from "./homeLang";
import Brand from "../../components/Brand/Brand";
import { SceneInView, SceneWindow } from "./NeedScenes";
import "./needs.css";

// Where the section pins beside its scene; must match the breakpoint in needs.css.
export const SIDE_BY_SIDE = "(min-width: 961px)";
// The pinned stretch, in steps of scroll: one from each thought to the next,
// and half of one on the last before the page moves on.
const STOPS = CONTENT.it.NEEDS.length - 1 + 0.5;

const HEADING = {
  it: "Durante una giornata ti capita mai di pensare?",
  en: "Ever catch yourself thinking this during the day?",
};

// "Durante una giornata ti capita mai di pensare?": an advisor's day as a timeline. On wide screens
// the section pins with the whole day in view: every bit of scroll draws the
// track on towards the next time, and when it gets there that thought and its
// AVRAH scene take the stage. A click on a thought takes the page to that
// place in the day, so the scroll still decides what is on stage. On narrow
// screens the thoughts stack, each with its answer and its scene.
export default function NeedsSection() {
  const { NEEDS } = useContent();
  const lang = useHomeLang();
  const [active, setActive] = useState(0);
  const section = useRef(null);
  const pin = useRef(null);
  const day = useRef(null);
  const goTo = useDayOnScroll({ section, pin, day }, setActive);
  return (
    <section ref={section} aria-labelledby="hf-h-needs" className="nd" style={{ "--stops": STOPS }}>
      <div ref={pin} className="hf-wrap nd-pin">
        <div className="nd-side">
          <h2 id="hf-h-needs" className="t-h2">{HEADING[lang]}</h2>
          <ol ref={day} className="nd-day">
            {NEEDS.map((n, i) => (
              <li key={n.time} style={{ "--i": i }} className={i < active ? "nd-i nd-past" : "nd-i"} aria-current={i === active ? "step" : undefined}>
                <span className="nd-t">{n.time}</span>
                <div className="nd-text">
                  <p className="nd-q">
                    {goTo ? <button type="button" className="nd-go" onClick={() => goTo(i)}><span className="nd-words">{n.quote}</span></button> : n.quote}
                  </p>
                  <p className="nd-a"><Brand>{n.answer}</Brand></p>
                </div>
                <SceneInView index={i} />
              </li>
            ))}
          </ol>
        </div>
        <SceneWindow index={active} />
      </div>
    </section>
  );
}

// While the section pins beside its scene, reads how far the pin has
// travelled, in steps: the track is drawn to that point (`--at` on the day)
// and `onStep` gets the last thought it has reached. Worked out from where the
// pin sits, so a jump (End key, an anchor link, a click on a thought) lands on
// the right thought. Returns how to take the page to a thought; on narrow
// screens it stays off and returns null: the thoughts are all on the page
// already. A layout effect, so it stops following in the same commit that
// takes the section off the page: a frame read in between would find the
// section gone.
function useDayOnScroll({ section, pin, day }, onStep) {
  const [following, setFollowing] = useState(false);
  useLayoutEffect(() => {
    const sideBySide = window.matchMedia(SIDE_BY_SIDE);
    let frame = 0;
    const read = () => {
      frame = 0;
      const { box, pinned, runway } = stretch(section.current, pin.current);
      const at = Math.min(Math.max((pinned.top - box.top) / runway, 0), 1) * STOPS;
      day.current.style.setProperty("--at", at.toFixed(3));
      onStep(Math.min(Math.floor(at), CONTENT.it.NEEDS.length - 1));
    };
    const schedule = () => { frame ||= requestAnimationFrame(read); };
    const follow = () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      setFollowing(sideBySide.matches);
      if (!sideBySide.matches) return;
      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", schedule);
      schedule();
    };
    follow();
    sideBySide.addEventListener("change", follow);
    return () => {
      sideBySide.removeEventListener("change", follow);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(frame);
    };
  }, [section, pin, day, onStep]);

  // `read` the other way round: scrolls the page, at once, to where thought
  // `i` takes the stage, a pixel in so rounding cannot leave the one before.
  const goTo = useCallback((i) => {
    const { box, runway } = stretch(section.current, pin.current);
    const stuckAt = parseFloat(getComputedStyle(pin.current).top);
    if (!Number.isFinite(stuckAt)) throw new Error("NeedsSection: needs.css does not stick the pin under the header");
    window.scrollTo({ top: window.scrollY + box.top - stuckAt + (i / STOPS) * runway + 1 });
  }, [section, pin]);

  return following ? goTo : null;
}

// The pinned stretch as it stands: the section's box, the pin's, and the
// scroll it takes the pin to travel from the section's top to its bottom.
function stretch(section, pin) {
  const box = section.getBoundingClientRect();
  const pinned = pin.getBoundingClientRect();
  const runway = box.height - pinned.height;
  if (runway <= 0) throw new Error("NeedsSection: needs.css does not pin the section at " + SIDE_BY_SIDE);
  return { box, pinned, runway };
}
